import { api } from './api-client';

// ════════════════════════════════════════════════════════════════════
// push.ts — frontend helper for Web Push subscription lifecycle.
//
// Three concerns split into focused functions:
//   1. registerServiceWorker — idempotent register of /sw.js
//   2. subscribeToPush       — pushManager.subscribe with VAPID key
//   3. sendSubscriptionToBackend — POST to /api/notifications/subscribe
//   4. unsubscribeFromPush   — both browser-side + server-side cleanup
//
// Step 2 does NOT invoke any of these automatically beyond
// registerServiceWorker (called once in App.tsx, prod-only). The actual
// subscribe flow runs in Step 3 (PushPermissionPrompt component) and
// is gated on Notification.permission === 'granted'.
//
// All functions are no-ops when the browser doesn't support the
// required APIs — they return null/undefined and never throw, so
// callers can use them without feature-detect boilerplate.
// ════════════════════════════════════════════════════════════════════

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (err) {
    console.error('[push] SW registration failed:', err);
    return null;
  }
}

export async function subscribeToPush(
  registration: ServiceWorkerRegistration
): Promise<PushSubscription | null> {
  if (!VAPID_PUBLIC_KEY) {
    console.warn('[push] VITE_VAPID_PUBLIC_KEY missing — cannot subscribe');
    return null;
  }
  if (!('pushManager' in registration)) return null;

  try {
    const existing = await registration.pushManager.getSubscription();
    if (existing) return existing;

    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  } catch (err) {
    // Most common: user denied permission. Also: pushManager unavailable.
    console.error('[push] subscribe failed:', err);
    return null;
  }
}

export async function sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  const endpoint = subscription.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!endpoint || !p256dh || !auth) {
    throw new Error('subscription missing required keys');
  }
  await api.notifications.subscribe({
    endpoint,
    p256dh,
    auth,
    user_agent:
      typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : undefined,
  });
}

export async function unsubscribeFromPush(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  try {
    await subscription.unsubscribe();
  } catch (err) {
    console.error('[push] browser unsubscribe failed:', err);
  }
  try {
    await api.notifications.unsubscribe(endpoint);
  } catch (err) {
    console.error('[push] server unsubscribe failed:', err);
  }
}

// Standard urlBase64ToUint8Array per the web-push docs. PushManager
// requires the application server key as a BufferSource backed by an
// ArrayBuffer (not the broader ArrayBufferLike — SharedArrayBuffer is
// rejected). Allocate the ArrayBuffer explicitly so TS narrows the
// Uint8Array generic parameter correctly under TS 5.7+ strict mode.
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const output = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}
